import { Heart } from "lucide-react";
import { motion } from "framer-motion";
import { useStore } from "@/context/StoreContext";
import { inr } from "@/lib/api";

export const Price = ({ p, size = "md", light }) => {
  const sale = p.sale_price && p.sale_price < p.price;
  return (
    <div className="flex items-baseline gap-2.5 flex-wrap" data-testid={`price-${p.slug}`}>
      <span className={`font-extrabold tracking-tight ${size === "lg" ? "text-[28px] sm:text-[30px]" : "text-[20px]"}`}>{inr(p.effective_price)}</span>
      {sale && (
        <>
          <span className={`line-through text-[15px] ${light ? "text-pearl/60" : "text-plum/50"}`}>{inr(p.price)}</span>
          <span className="text-[11px] font-extrabold uppercase tracking-wider px-2 py-1 rounded-md bg-ruby text-white">
            Save {Math.round((1 - p.sale_price / p.price) * 100)}%
          </span>
        </>
      )}
    </div>
  );
};

export const Availability = ({ p, light, variant }) => {
  const v = variant && p.variants?.find((x) => x.label === variant);
  const n = v ? v.stock : p.available;
  const txt = n <= 0 ? "Sold out" : n <= 3 ? `Only ${n} left` : "In stock";
  const dot = n <= 0 ? "bg-ruby" : n <= 3 ? "bg-[#C98A1B]" : "bg-emerald";
  const sizes = p.variants?.filter((x) => x.stock > 0).length;
  return (
    <p className={`flex items-center gap-2 text-[14px] font-semibold ${light ? "text-pearl/85" : "text-plum/75"}`} data-testid={`availability-${p.slug}`}>
      <span className={`w-2 h-2 rounded-full ${dot}`} />
      {txt}
      {!variant && p.variants?.length > 0 && n > 0 && <span className="font-medium"> · {sizes} {p.variant_name.toLowerCase()}s</span>}
    </p>
  );
};

export const WishButton = ({ p, className = "" }) => {
  const { wish, toggleWish } = useStore();
  const on = wish.includes(p.id);
  return (
    <motion.button
      whileTap={{ scale: 0.85 }}
      onClick={() => toggleWish(p.id)}
      aria-label={on ? "Remove from wishlist" : "Save to wishlist"}
      data-testid={`wishlist-btn-${p.slug}`}
      className={`grid place-items-center w-11 h-11 rounded-[14px] bg-pearl/90 backdrop-blur text-plum hover:bg-white transition-colors ${className}`}
    >
      <Heart className={`w-[19px] h-[19px] transition-colors ${on ? "fill-ruby text-ruby" : ""}`} strokeWidth={2} />
    </motion.button>
  );
};

export const VariantChips = ({ p, value, onChange, light, size = "sm" }) =>
  p.variants?.length ? (
    <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={p.variant_name}>
      {p.variants.map((v) => {
        const active = v.label === value;
        const out = v.stock <= 0;
        return (
          <button
            key={v.label}
            disabled={out}
            onClick={() => onChange(v.label)}
            data-testid={`variant-${p.slug}-${v.label.replace(/\s+/g, "-").toLowerCase()}`}
            aria-checked={active}
            role="radio"
            className={`rounded-[12px] border font-bold transition-colors ${size === "lg" ? "px-4 h-12 text-[15px]" : "px-3 h-10 text-[13px]"} ${
              active
                ? light ? "bg-pearl text-plum border-pearl" : "bg-plum text-pearl border-plum"
                : light ? "border-pearl/35 text-pearl hover:border-pearl" : "border-plum/20 hover:border-plum"
            } ${out ? "opacity-40 line-through cursor-not-allowed" : ""}`}
          >
            {v.label}
          </button>
        );
      })}
    </div>
  ) : null;
